import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { Check, ChevronDown, Pin } from "lucide-react";
import type { DemandSummaryOption } from "@/lib/data/requests/reports/reports-demand-summary";

type Props = {
  id: string;
  roles: DemandSummaryOption[];
  selectedRoleId?: string;
  pinnedRoleId?: string | null;
  canPin: boolean;
  isPinning: boolean;
  onSelect: (roleId?: string) => void;
  onPin: (roleId: string | null) => void;
};

export function ReportRoleDropdown({
  id,
  roles,
  selectedRoleId,
  pinnedRoleId,
  canPin,
  isPinning,
  onSelect,
  onPin,
}: Props) {
  const selected = roles.find((role) => role.id === selectedRoleId);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className="h-10 w-full justify-between rounded-md px-3 font-normal"
        >
          {selected?.label ?? "Choose a matter role"}
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-80 w-[var(--radix-dropdown-menu-trigger-width)] min-w-64 overflow-y-auto"
      >
        <DropdownMenuItem
          onSelect={() => {
            onSelect(undefined);
          }}
        >
          {pinnedRoleId ? "Default role" : "Choose a matter role"}
        </DropdownMenuItem>
        {roles.map((role) => {
          const pinned = role.id === pinnedRoleId;
          return (
            <div key={role.id} className="flex items-center gap-1">
              <DropdownMenuItem
                className="min-w-0 flex-1"
                onSelect={() => {
                  onSelect(role.id);
                }}
              >
                <Check
                  className={
                    role.id === selectedRoleId ? "h-4 w-4" : "h-4 w-4 opacity-0"
                  }
                />
                <span className="truncate">{role.label}</span>
              </DropdownMenuItem>
              {canPin ? (
                <DropdownMenuItem
                  className="h-8 w-8 justify-center p-0"
                  disabled={isPinning}
                  aria-label={`${pinned ? "Unpin" : "Pin"} ${role.label} ${pinned ? "as the shared default" : "as the default for everyone"}`}
                  title={
                    pinned
                      ? "Unpin the shared default"
                      : "Pin as the default for everyone"
                  }
                  onSelect={(event) => {
                    event.preventDefault();
                    onPin(pinned ? null : role.id);
                  }}
                >
                  <Pin
                    className={
                      pinned
                        ? "text-primary h-4 w-4 fill-current"
                        : "text-muted-foreground h-4 w-4"
                    }
                  />
                </DropdownMenuItem>
              ) : pinned ? (
                <Pin
                  className="text-primary mr-2 h-4 w-4 fill-current"
                  aria-label="Pinned default role"
                />
              ) : null}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
